
import { Vue, Component, Prop, Watch, PropSync } from "vue-property-decorator";
import { namespace } from "vuex-class";
// Interfaces
import { RedactionRectangle } from "@/interfaces/RedactionRectangle";
import { UserCertificateVm } from "@/interfaces/UserCertificateVm";
import { CustomizationVm } from "@/interfaces/CustomizationVm";
import { PortalUserVM } from "@/interfaces/PortalUserVM";

const auth = namespace("auth");
const customization = namespace("customization");
const ux = namespace("ux");

@Component({
  components: {
  }
})
export default class ImageAnnotationCanvas extends Vue {
  @customization.Action loadCustomizationSettings!: any;
  @customization.Getter customizationSettings!: CustomizationVm;
  @auth.Getter isAdmin: any;
  @auth.State portalUser!: PortalUserVM;
  @ux.Action setDocumentAiAnalysisConfirmed!: any;
  @ux.Getter isDocumentAiAnalysisConfirmed!: boolean;

  @Prop() certificate!: UserCertificateVm | null;
  @PropSync("busyOnOcr") syncedBusyOnOcr!: boolean;

  showAiConfirmDialog = false;
  confirmedRedactionsComplete = false;

  get hasAlreadyConfirmedAi(): boolean {
    return this.isDocumentAiAnalysisConfirmed;
  }

  canvasRef: HTMLCanvasElement | null = null;
  imageRef: HTMLImageElement | null = null;
  imageContentRef: HTMLDivElement | null = null;
  ctx: CanvasRenderingContext2D | null = null;
  resizeObserver: ResizeObserver | null = null;
  resizeTimeout: ReturnType<typeof setTimeout> | null = null;

  redactions: RedactionRectangle[] = [];
  imageLoaded = false;
  displayDimensions = { width: 0, height: 0 };
  lastProcessedDimensions = { width: 0, height: 0 };

  isDrawing = false;
  isResizing = false;
  isDragging = false;
  isRectangleInteractionActive = false;
  autoScrollTimeout: ReturnType<typeof setTimeout> | null = null;
  autoScrollAnimationFrame: number | null = null;
  autoScrollDirection = 0;
  lastMouseX = 0;
  lastMouseY = 0;
  selectedRectIndex = -1;
  selectedHandle = "";
  drawStart = { x: 0, y: 0 };
  dragOffset = { x: 0, y: 0 };

  HANDLE_SIZE = 8;
  REDACTION_COLOR = "rgba(255, 100, 100, 0.3)";
  BORDER_COLOR = "#ff6464";
  HANDLE_COLOR = "#ff6464";

  async mounted() {
    // if (!this.portalUser || this.portalUser.aiApprovalRequired) {
    //   router.push("/aiApproval");
    //   return;
    // }

    await this.loadCustomizationSettings();

    this.canvasRef = this.$refs.canvasRef as HTMLCanvasElement;
    this.imageRef = this.$refs.imageRef as HTMLImageElement;
    this.imageContentRef = this.$refs.imageContentRef as HTMLDivElement;
    this.ctx = this.canvasRef?.getContext("2d") ?? null;
    this.syncFromCertificate();
    // Debounced ResizeObserver to handle dialog resizes without triggering infinite loops
    const wrapper = (this.$el as HTMLElement).querySelector('.image-wrapper') as HTMLElement | null;
    if (wrapper && typeof ResizeObserver !== 'undefined') {
      this.resizeObserver = new ResizeObserver(() => {
        // Only trigger setupCanvas if image is loaded and debounce to prevent rapid loops
        if (this.imageLoaded) {
          if (this.resizeTimeout) {
            clearTimeout(this.resizeTimeout);
          }
          this.resizeTimeout = setTimeout(() => {
            this.setupCanvas();
          }, 150);
        }
      });
      this.resizeObserver.observe(wrapper);
    }
    window.addEventListener("resize", this.onWindowResize);
  }

  beforeDestroy() {
    window.removeEventListener("resize", this.onWindowResize);
    this.stopAutoScroll();
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
      this.resizeObserver = null;
    }
    if (this.resizeTimeout) {
      clearTimeout(this.resizeTimeout);
      this.resizeTimeout = null;
    }
  }

  @Watch("certificate", { deep: true })
  onCertificateChanged() {
    if (this.isRectangleInteractionActive) {
      return;
    }

    this.syncFromCertificate();
    this.redraw();
  }

  get imageUrl(): string | null {
    return this.certificate?.pdfPreviewImageUrl ?? null;
  }

  get isMarkingEnabled(): boolean {
    return !!this.customizationSettings?.enableAi;
  }

  get redactionRectangles(): RedactionRectangle[] {
    return this.certificate?.redactionRectangles ?? [];
  }

  syncFromCertificate() {
    this.imageLoaded = false;
    this.lastProcessedDimensions = { width: 0, height: 0 }; // Reset to force setupCanvas on next image load
    this.redactions = [...(this.redactionRectangles ?? [])];
    this.$nextTick(() => {
      // re-acquire refs
      this.canvasRef = this.$refs.canvasRef as HTMLCanvasElement | null;
      this.imageRef = this.$refs.imageRef as HTMLImageElement | null;
      this.imageContentRef = this.$refs.imageContentRef as HTMLDivElement | null;

      if (this.imageRef && this.imageUrl) {
        // Direct assignment with cache-bust to force reload
        const sep = this.imageUrl.includes("?") ? "&" : "?";
        const srcWithBust = this.imageUrl + sep + "t=" + Date.now();
        this.imageRef.src = srcWithBust;
        // Note: setupCanvas() will be called from onImageLoaded() after the image dimensions are available
      }
    });
  }

  onImageLoaded() {
    this.imageLoaded = true;
    // Defer setupCanvas with extra delay to ensure layout is fully computed and dialog has rendered
    setTimeout(() => {
      this.$nextTick(() => {
        this.setupCanvas();
        this.redraw();
      });
    }, 50);
  }

  onImageError() {
    // Image failed to load - handled silently
  }

  setupCanvas() {
    if (!this.canvasRef || !this.imageRef) return;

    // Measure available width from the outer .image-wrapper, not the inner .image-content
    const imageWrapper = (this.$el as HTMLElement).querySelector('.image-wrapper') as HTMLElement | null;
    const wrapper = imageWrapper || this.canvasRef.parentElement;
    if (!wrapper) return;

    const availableWidth = wrapper.clientWidth || this.canvasRef.parentElement?.clientWidth || 0;
    const naturalWidth = this.imageRef.naturalWidth || 1;
    const naturalHeight = this.imageRef.naturalHeight || 1;
    const ratio = naturalHeight / naturalWidth;
    // allow scaling up to available width so image grows when dialog expands
    const displayWidth = Math.max(1, availableWidth);
    const displayHeight = Math.max(1, Math.round(displayWidth * ratio));

    const isImageLoaded = this.imageRef.naturalWidth > 0;

    // Guard: Only proceed if dimensions actually changed to prevent ResizeObserver loop
    const dimensionsChanged =
      displayWidth !== this.lastProcessedDimensions.width ||
      displayHeight !== this.lastProcessedDimensions.height;

    if (!dimensionsChanged) {
      return;
    }

    this.lastProcessedDimensions = { width: displayWidth, height: displayHeight };
    this.displayDimensions = {
      width: displayWidth,
      height: displayHeight
    };

    this.imageRef.style.width = `${displayWidth}px`;
    this.imageRef.style.height = `${displayHeight}px`;
    if (this.imageContentRef) {
      this.imageContentRef.style.width = `${displayWidth}px`;
      this.imageContentRef.style.height = `${displayHeight}px`;
    }
    this.canvasRef.width = displayWidth;
    this.canvasRef.height = displayHeight;
    this.canvasRef.style.width = `${displayWidth}px`;
    this.canvasRef.style.height = `${displayHeight}px`;

    this.ctx = this.canvasRef.getContext("2d");
    this.redraw();
  }

  onWindowResize = () => {
    // Debounce window resize to avoid rapid setupCanvas calls
    if (this.resizeTimeout) {
      clearTimeout(this.resizeTimeout);
    }
    this.resizeTimeout = setTimeout(() => {
      this.setupCanvas();
    }, 100);
  };

  onCanvasMouseDown(event: MouseEvent) {
    if (!this.isMarkingEnabled || !this.imageLoaded || !this.canvasRef) {
      if (this.canvasRef) {
        this.canvasRef.style.cursor = "not-allowed";
      }
      return;
    }

    const rect = this.canvasRef.getBoundingClientRect();
    const mouseX = event.clientX - rect.left;
    const mouseY = event.clientY - rect.top;
    const wrapper = (this.$el as HTMLElement).querySelector(".image-wrapper") as HTMLElement | null;
    this.lastMouseX = mouseX;
    this.lastMouseY = wrapper ? event.clientY - wrapper.getBoundingClientRect().top : mouseY;

    const hitResult = this.checkHitTest(mouseX, mouseY);

    if (hitResult.type === "handle") {
      this.isRectangleInteractionActive = true;
      this.isResizing = true;
      this.selectedRectIndex = hitResult.index;
      this.selectedHandle = hitResult.handle;
      // Store drawStart with scroll offset so delta calculations work correctly during scroll
      const wrapper = (this.$el as HTMLElement).querySelector(".image-wrapper") as HTMLElement | null;
      const scrollAdjustedY = this.lastMouseY + (wrapper?.scrollTop || 0);
      this.drawStart = { x: mouseX, y: scrollAdjustedY };
    } else if (hitResult.type === "rectangle") {
      this.isRectangleInteractionActive = true;
      this.isDragging = true;
      this.selectedRectIndex = hitResult.index;
      const r = this.redactions[hitResult.index];
      const pixelRect = this.normalizedToPixel(r);
      this.dragOffset = {
        x: mouseX - pixelRect.x,
        y: mouseY - pixelRect.y
      };
    } else {
      this.isRectangleInteractionActive = false;
      this.isDrawing = true;
      this.selectedRectIndex = -1;
      this.drawStart = { x: mouseX, y: mouseY };
    }

    this.canvasRef.focus();
  }

  onCanvasMouseMove(event: MouseEvent) {
    if (!this.isMarkingEnabled) {
      if (this.canvasRef) {
        this.canvasRef.style.cursor = "not-allowed";
      }
      return;
    }

    if (!this.imageLoaded || !this.canvasRef) return;

    const rect = this.canvasRef.getBoundingClientRect();
    const mouseX = event.clientX - rect.left;
    const mouseY = event.clientY - rect.top;
    const wrapper = (this.$el as HTMLElement).querySelector(".image-wrapper") as HTMLElement | null;
    this.lastMouseX = mouseX;
    this.lastMouseY = wrapper ? event.clientY - wrapper.getBoundingClientRect().top : mouseY;

    if (this.isDrawing) {
      this.updateAutoScroll();
      this.redraw();
      this.drawPreviewRectangle(this.drawStart.x, this.drawStart.y, mouseX, mouseY);
    } else if (this.isResizing) {
      this.updateAutoScroll();
      const wrapper = (this.$el as HTMLElement).querySelector(".image-wrapper") as HTMLElement | null;
      const scrollAdjustedY = this.lastMouseY + (wrapper?.scrollTop || 0);
      this.resizeRectangle(mouseX, scrollAdjustedY);
      this.redraw();
    } else if (this.isDragging) {
      this.updateAutoScroll();
      const wrapper = (this.$el as HTMLElement).querySelector(".image-wrapper") as HTMLElement | null;
      const scrollAdjustedY = this.lastMouseY + (wrapper?.scrollTop || 0);
      this.dragRectangle(mouseX, scrollAdjustedY);
      this.redraw();
    } else {
      const hitResult = this.checkHitTest(mouseX, mouseY);
      this.updateCursor(hitResult);
    }
  }

  onCanvasMouseUp(event: MouseEvent) {
    const wrapper = (this.$el as HTMLElement).querySelector(".image-wrapper") as HTMLElement | null;
    const savedScrollTop = wrapper ? wrapper.scrollTop : 0;

    this.stopAutoScroll();

    if (!this.isMarkingEnabled) {
      this.isDrawing = false;
      this.isResizing = false;
      this.isDragging = false;
      this.selectedRectIndex = -1;
      if (this.canvasRef) {
        this.canvasRef.style.cursor = "not-allowed";
      }
      return;
    }

    if (this.isDrawing) {
      const rect = this.canvasRef!.getBoundingClientRect();
      const endX = event.clientX - rect.left;
      const endY = event.clientY - rect.top;

      const pixelRect = this.normalizeCoordinates(this.drawStart.x, this.drawStart.y, endX, endY);
      if (pixelRect.width > 0.01 && pixelRect.height > 0.01) {
        this.redactions.push({
          id: this.generateId(),
          ...pixelRect
        });
        this.emitChanges();
      }
    }

    this.isDrawing = false;
    this.isResizing = false;
    this.isDragging = false;
    this.isRectangleInteractionActive = false;
    this.selectedRectIndex = -1;
    this.redraw();

    // Restore scroll position after all updates and async operations complete
    const restoreScroll = () => {
      if (wrapper && wrapper.scrollTop !== savedScrollTop) {
        wrapper.scrollTop = savedScrollTop;
      }
    };
    
    this.$nextTick(() => {
      // First restoration in nextTick
      restoreScroll();
      // Second restoration with timeout to catch any delayed updates
      setTimeout(restoreScroll, 50);
    });
  }

  onCanvasMouseLeave() {
    const wrapper = (this.$el as HTMLElement).querySelector(".image-wrapper") as HTMLElement | null;
    const savedScrollTop = wrapper ? wrapper.scrollTop : 0;

    this.stopAutoScroll();

    if (!this.isMarkingEnabled) {
      this.isDrawing = false;
      this.isResizing = false;
      this.isDragging = false;
      this.isRectangleInteractionActive = false;
      this.selectedRectIndex = -1;
      if (this.canvasRef) {
        this.canvasRef.style.cursor = "not-allowed";
      }
      return;
    }

    if (this.isDrawing) {
      this.isDrawing = false;
      this.isRectangleInteractionActive = false;
      this.redraw();
    }

    // Restore scroll position after all updates
    const restoreScroll = () => {
      if (wrapper && wrapper.scrollTop !== savedScrollTop) {
        wrapper.scrollTop = savedScrollTop;
      }
    };
    
    this.$nextTick(() => {
      restoreScroll();
      setTimeout(restoreScroll, 50);
    });
  }

  onKeyDown(event: KeyboardEvent) {
    if (!this.isMarkingEnabled) {
      event.preventDefault();
      return;
    }

    if (event.key === "Delete" && this.selectedRectIndex >= 0) {
      this.removeRedaction(this.selectedRectIndex);
      event.preventDefault();
    }
  }

  checkHitTest(x: number, y: number): { type: "handle" | "rectangle" | "none"; index: number; handle: string } {
    for (let i = this.redactions.length - 1; i >= 0; i--) {
      const r = this.redactions[i];
      const pixelRect = this.normalizedToPixel(r);
      const tolerance = this.HANDLE_SIZE;

      // Check edge handles first with rectangular regions
      if (x >= pixelRect.x && x <= pixelRect.x + pixelRect.width && Math.abs(y - pixelRect.y) <= tolerance) {
        return { type: "handle", index: i, handle: "top" };
      }
      if (x >= pixelRect.x && x <= pixelRect.x + pixelRect.width && Math.abs(y - (pixelRect.y + pixelRect.height)) <= tolerance) {
        return { type: "handle", index: i, handle: "bottom" };
      }
      if (y >= pixelRect.y && y <= pixelRect.y + pixelRect.height && Math.abs(x - pixelRect.x) <= tolerance) {
        return { type: "handle", index: i, handle: "left" };
      }
      if (y >= pixelRect.y && y <= pixelRect.y + pixelRect.height && Math.abs(x - (pixelRect.x + pixelRect.width)) <= tolerance) {
        return { type: "handle", index: i, handle: "right" };
      }

      // Check corner handles with circular regions
      const corners = [
        { name: "tl", pos: { x: pixelRect.x, y: pixelRect.y } },
        { name: "tr", pos: { x: pixelRect.x + pixelRect.width, y: pixelRect.y } },
        { name: "bl", pos: { x: pixelRect.x, y: pixelRect.y + pixelRect.height } },
        { name: "br", pos: { x: pixelRect.x + pixelRect.width, y: pixelRect.y + pixelRect.height } }
      ];

      for (const corner of corners) {
        const dist = Math.sqrt(Math.pow(x - corner.pos.x, 2) + Math.pow(y - corner.pos.y, 2));
        if (dist <= tolerance) {
          return { type: "handle", index: i, handle: corner.name };
        }
      }

      // Check if inside rectangle
      if (
        x >= pixelRect.x &&
        x <= pixelRect.x + pixelRect.width &&
        y >= pixelRect.y &&
        y <= pixelRect.y + pixelRect.height
      ) {
        return { type: "rectangle", index: i, handle: "" };
      }
    }

    return { type: "none", index: -1, handle: "" };
  }

  getHandlePositions(pixelRect: { x: number; y: number; width: number; height: number }) {
    const { x, y, width, height } = pixelRect;
    return {
      tl: { x, y },
      tr: { x: x + width, y },
      bl: { x, y: y + height },
      br: { x: x + width, y: y + height },
      top: { x: x + width / 2, y },
      bottom: { x: x + width / 2, y: y + height },
      left: { x, y: y + height / 2 },
      right: { x: x + width, y: y + height / 2 }
    };
  }

  updateCursor(hitResult: any) {
    if (!this.isMarkingEnabled) {
      if (this.canvasRef) {
        this.canvasRef.style.cursor = "not-allowed";
      }
      return;
    }

    const cursorStyle =
      hitResult.type === "handle"
        ? {
            tl: "nwse-resize",
            tr: "nesw-resize",
            bl: "nesw-resize",
            br: "nwse-resize",
            top: "ns-resize",
            bottom: "ns-resize",
            left: "ew-resize",
            right: "ew-resize"
          }[hitResult.handle] || "default"
        : hitResult.type === "rectangle"
        ? "move"
        : "crosshair";

    if (this.canvasRef) {
      this.canvasRef.style.cursor = cursorStyle;
    }
  }

  resizeRectangle(mouseX: number, mouseY: number) {
    if (this.selectedRectIndex < 0) return;

    const r = this.redactions[this.selectedRectIndex];
    const pixelRect = this.normalizedToPixel(r);
    const deltaX = mouseX - this.drawStart.x;
    const deltaY = mouseY - this.drawStart.y;

    let newX = pixelRect.x;
    let newY = pixelRect.y;
    let newW = pixelRect.width;
    let newH = pixelRect.height;

    // Only modify the edges/corners that are being resized
    switch (this.selectedHandle) {
      case "tl":
        newX += deltaX;
        newY += deltaY;
        newW -= deltaX;
        newH -= deltaY;
        break;
      case "tr":
        newY += deltaY;
        newW += deltaX;
        newH -= deltaY;
        break;
      case "bl":
        newX += deltaX;
        newW -= deltaX;
        newH += deltaY;
        break;
      case "br":
        newW += deltaX;
        newH += deltaY;
        break;
      case "top":
        newY += deltaY;
        newH -= deltaY;
        break;
      case "bottom":
        newH += deltaY;
        break;
      case "left":
        newX += deltaX;
        newW -= deltaX;
        break;
      case "right":
        newW += deltaX;
        break;
    }

    newX = Math.max(0, newX);
    newY = Math.max(0, newY);
    newW = Math.max(10, newW);
    newH = Math.max(10, newH);
    newX = Math.min(newX, this.displayDimensions.width - newW);
    newY = Math.min(newY, this.displayDimensions.height - newH);

    const normalized = this.pixelToNormalized({ x: newX, y: newY, width: newW, height: newH });
    this.redactions[this.selectedRectIndex] = {
      ...r,
      ...normalized
    };

    this.drawStart = { x: mouseX, y: mouseY };
    this.emitChanges();
  }

  dragRectangle(mouseX: number, mouseY: number) {
    if (this.selectedRectIndex < 0) return;

    const r = this.redactions[this.selectedRectIndex];
    const pixelRect = this.normalizedToPixel(r);

    let newX = mouseX - this.dragOffset.x;
    let newY = mouseY - this.dragOffset.y;
    newX = Math.max(0, Math.min(newX, this.displayDimensions.width - pixelRect.width));
    newY = Math.max(0, Math.min(newY, this.displayDimensions.height - pixelRect.height));

    const normalized = this.pixelToNormalized({ x: newX, y: newY, width: pixelRect.width, height: pixelRect.height });
    this.redactions[this.selectedRectIndex] = {
      ...r,
      ...normalized
    };

    this.emitChanges();
  }

  removeRedaction(index: number) {
    if (!this.isMarkingEnabled) {
      return;
    }

    this.redactions.splice(index, 1);
    this.selectedRectIndex = -1;
    this.emitChanges();
    this.redraw();
  }

  updateAutoScroll() {
    const wrapper = (this.$el as HTMLElement).querySelector(".image-wrapper") as HTMLElement | null;
    if (!wrapper || (!this.isDragging && !this.isResizing && !this.isDrawing)) {
      this.stopAutoScroll();
      return;
    }

    const edgeDistance = 30;
    const direction = this.lastMouseY < edgeDistance ? -1 : this.lastMouseY > wrapper.clientHeight - edgeDistance ? 1 : 0;

    if (direction === 0) {
      this.stopAutoScroll();
      return;
    }

    const maxScrollTop = Math.max(0, wrapper.scrollHeight - wrapper.clientHeight);
    if (direction < 0 && wrapper.scrollTop <= 0) {
      this.stopAutoScroll();
      return;
    }
    if (direction > 0 && wrapper.scrollTop >= maxScrollTop) {
      this.stopAutoScroll();
      return;
    }

    this.autoScrollDirection = direction;
    if (!this.autoScrollTimeout) {
      this.startAutoScroll();
    }
  }

  startAutoScroll() {
    const tick = () => {
      const wrapper = (this.$el as HTMLElement).querySelector(".image-wrapper") as HTMLElement | null;
      if (!wrapper || (!this.isDragging && !this.isResizing && !this.isDrawing)) {
        this.stopAutoScroll();
        return;
      }

      const edgeDistance = 30;
      const nextDirection = this.lastMouseY < edgeDistance ? -1 : this.lastMouseY > wrapper.clientHeight - edgeDistance ? 1 : 0;

      if (nextDirection === 0) {
        this.stopAutoScroll();
        return;
      }

      const maxScrollTop = Math.max(0, wrapper.scrollHeight - wrapper.clientHeight);
      if (nextDirection < 0 && wrapper.scrollTop <= 0) {
        this.stopAutoScroll();
        return;
      }
      if (nextDirection > 0 && wrapper.scrollTop >= maxScrollTop) {
        this.stopAutoScroll();
        return;
      }

      this.autoScrollDirection = nextDirection;
      const scrollStep = 1;
      const previousScrollTop = wrapper.scrollTop;
      wrapper.scrollTop = Math.min(maxScrollTop, Math.max(0, wrapper.scrollTop + nextDirection * scrollStep));
      const scrollDelta = wrapper.scrollTop - previousScrollTop;

      // Update positions and redraw based on interaction type
      if (this.isDragging && this.selectedRectIndex >= 0) {
        const currentRedaction = this.redactions[this.selectedRectIndex];
        if (currentRedaction) {
          const pixelRect = this.normalizedToPixel(currentRedaction);
          const actualMouseY = this.lastMouseY + wrapper.scrollTop;
          const newY = Math.max(0, Math.min(actualMouseY - this.dragOffset.y, this.displayDimensions.height - pixelRect.height));
          const normalized = this.pixelToNormalized({ x: pixelRect.x, y: newY, width: pixelRect.width, height: pixelRect.height });
          this.redactions[this.selectedRectIndex] = {
            ...currentRedaction,
            ...normalized
          };
          this.emitChanges();
        }
      } else if (this.isResizing && this.selectedRectIndex >= 0) {
        const actualMouseY = this.lastMouseY + wrapper.scrollTop;
        this.resizeRectangle(this.lastMouseX, actualMouseY);
        this.emitChanges();
      }

      // Always redraw when scrolling
      this.redraw();

      // For drawing, convert wrapper-relative lastMouseY to canvas-relative by adding scrollTop
      if (this.isDrawing) {
        const canvasMouseY = this.lastMouseY + wrapper.scrollTop;
        this.drawPreviewRectangle(this.drawStart.x, this.drawStart.y, this.lastMouseX, canvasMouseY);
      }

      this.autoScrollAnimationFrame = requestAnimationFrame(tick);
    };

    this.autoScrollAnimationFrame = requestAnimationFrame(tick);
  }

  stopAutoScroll() {
    this.autoScrollDirection = 0;
    if (this.autoScrollTimeout) {
      clearTimeout(this.autoScrollTimeout);
      this.autoScrollTimeout = null;
    }
    if (this.autoScrollAnimationFrame !== null) {
      cancelAnimationFrame(this.autoScrollAnimationFrame);
      this.autoScrollAnimationFrame = null;
    }
  }

  drawPreviewRectangle(x1: number, y1: number, x2: number, y2: number) {
    if (!this.ctx) return;

    const x = Math.min(x1, x2);
    const y = Math.min(y1, y2);
    const w = Math.abs(x2 - x1);
    const h = Math.abs(y2 - y1);

    this.ctx.fillStyle = this.REDACTION_COLOR;
    this.ctx.fillRect(x, y, w, h);

    this.ctx.strokeStyle = this.BORDER_COLOR;
    this.ctx.lineWidth = 2;
    this.ctx.strokeRect(x, y, w, h);
  }

  redraw() {
    if (!this.ctx || !this.canvasRef) return;

    this.ctx.clearRect(0, 0, this.canvasRef.width, this.canvasRef.height);

    for (let i = 0; i < this.redactions.length; i++) {
      const r = this.redactions[i];
      const pixelRect = this.normalizedToPixel(r);
      const isSelected = i === this.selectedRectIndex;

      this.ctx.fillStyle = isSelected ? "rgba(255, 100, 100, 0.4)" : this.REDACTION_COLOR;
      this.ctx.fillRect(pixelRect.x, pixelRect.y, pixelRect.width, pixelRect.height);

      this.ctx.strokeStyle = isSelected ? "#ff3333" : this.BORDER_COLOR;
      this.ctx.lineWidth = isSelected ? 3 : 2;
      this.ctx.strokeRect(pixelRect.x, pixelRect.y, pixelRect.width, pixelRect.height);

      if (isSelected) {
        this.drawHandles(pixelRect);
      }
    }
  }

  drawHandles(pixelRect: { x: number; y: number; width: number; height: number }) {
    if (!this.ctx) return;

    const handles = this.getHandlePositions(pixelRect);
    this.ctx.fillStyle = this.HANDLE_COLOR;

    for (const handlePos of Object.values(handles)) {
      this.ctx.fillRect(handlePos.x - this.HANDLE_SIZE / 2, handlePos.y - this.HANDLE_SIZE / 2, this.HANDLE_SIZE, this.HANDLE_SIZE);
    }
  }

  normalizeCoordinates(x1: number, y1: number, x2: number, y2: number) {
    const x = Math.min(x1, x2);
    const y = Math.min(y1, y2);
    const width = Math.abs(x2 - x1);
    const height = Math.abs(y2 - y1);

    return this.pixelToNormalized({ x, y, width, height });
  }

  pixelToNormalized(pixelRect: { x: number; y: number; width: number; height: number }) {
    const width = Math.max(this.displayDimensions.width, 1);
    const height = Math.max(this.displayDimensions.height, 1);

    return {
      x: pixelRect.x / width,
      y: pixelRect.y / height,
      width: pixelRect.width / width,
      height: pixelRect.height / height
    };
  }

  normalizedToPixel(r: RedactionRectangle): { x: number; y: number; width: number; height: number } {
    const width = Math.max(this.displayDimensions.width, 1);
    const height = Math.max(this.displayDimensions.height, 1);

    return {
      x: r.x * width,
      y: r.y * height,
      width: r.width * width,
      height: r.height * height
    };
  }

  generateId(): string {
    return `rect-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
  }

  emitChanges() {
    if (this.certificate) {
      this.certificate.redactionRectangles = this.redactions;
    }
    this.$emit("redactionsChanged", this.redactions);
  }

  onOcrClick() {
    if (!this.hasAlreadyConfirmedAi)
      this.showAiConfirmDialog = true;
    else
      this.$emit("ocrRequested:imageAnnotationCanvas", this.redactions);
  }

  onOcrClickConfirmed() {
    // Save confirmation to persistent storage
    this.setDocumentAiAnalysisConfirmed();
    // Emit OCR request with redactions
    this.$emit("ocrRequested:imageAnnotationCanvas", this.redactions);
    this.showAiConfirmDialog = false;
    this.confirmedRedactionsComplete = false;
  }
}
